import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    McDialog,
    McButton,
    McInput,
    McIcon,
    McLoadingIndicator,
    McTooltip
} from '@maersk-global/mds-react-wrapper';
import { NAVAS_N4_DICTIONARY } from '../utils/n4Dictionary';
import queryService from '../services/queryService';

interface MappingManagerDialogProps {
    open: boolean;
    onClose: () => void;
    currentMappings: Record<string, any>;
    onSave: (newMappings: Record<string, any>) => void;
    availableTables?: string[];
    terminalId?: string;
}

export const MappingManagerDialog: React.FC<MappingManagerDialogProps> = ({
    open,
    onClose,
    currentMappings,
    onSave,
    availableTables = [],
    terminalId
}) => {
    const latestOnSave = useRef(onSave);
    const latestOnClose = useRef(onClose);

    useEffect(() => { latestOnSave.current = onSave; }, [onSave]);
    useEffect(() => { latestOnClose.current = onClose; }, [onClose]);

    const [searchTerm, setSearchTerm] = useState('');
    const [mappings, setMappings] = useState<Record<string, any>>({});
    const [expandedTable, setExpandedTable] = useState<string | null>(null);
    const [tableColumns, setTableColumns] = useState<Record<string, string[]>>({});
    const [loadingColumns, setLoadingColumns] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setMappings(JSON.parse(JSON.stringify(currentMappings)));
        }
    }, [open, currentMappings]);

    const allTableNames = useMemo(() => {
        const dictKeys = Object.keys(NAVAS_N4_DICTIONARY);
        const unique = new Set([...dictKeys, ...availableTables]);
        return Array.from(unique).sort();
    }, [availableTables]);

    const filteredTables = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return allTableNames.filter(name => {
            const display = mappings[name]?.businessName || NAVAS_N4_DICTIONARY[name]?.title || name;
            return name.toLowerCase().includes(lowerSearch) || display.toLowerCase().includes(lowerSearch);
        });
    }, [allTableNames, searchTerm, mappings]);

    const handleExpandTable = async (tableName: string) => {
        if (expandedTable === tableName) {
            setExpandedTable(null);
            return;
        }

        setExpandedTable(tableName);

        if (!tableColumns[tableName]) {
            // Try to fetch columns if terminalId is present
            if (terminalId) {
                setLoadingColumns(tableName);
                try {
                    const columns = await queryService.exploreSchema(terminalId, tableName);
                    setTableColumns(prev => ({ ...prev, [tableName]: columns.map((c: any) => c.name) }));
                } catch (err) {
                    console.error(`Failed to fetch columns for ${tableName}`, err);
                    // Fallback to empty or maybe previously mapped columns if any
                    const mappedCols = mappings[tableName]?.columns ? Object.keys(mappings[tableName].columns) : [];
                    if (mappedCols.length > 0) {
                        setTableColumns(prev => ({ ...prev, [tableName]: mappedCols }));
                    }
                } finally {
                    setLoadingColumns(null);
                }
            } else {
                // If no terminal, we can only show what's already mapped
                const mappedCols = mappings[tableName]?.columns ? Object.keys(mappings[tableName].columns) : [];
                setTableColumns(prev => ({ ...prev, [tableName]: mappedCols }));
            }
        }
    };

    const handleTableMappingChange = (tableName: string, newValue: string) => {
        setMappings(prev => ({
            ...prev,
            [tableName]: {
                ...prev[tableName],
                businessName: newValue
            }
        }));
    };

    const handleColumnMappingChange = (tableName: string, colName: string, newValue: string) => {
        setMappings(prev => ({
            ...prev,
            [tableName]: {
                ...prev[tableName],
                columns: {
                    ...prev[tableName]?.columns,
                    [colName]: newValue
                }
            }
        }));
    };

    const handleResetTable = (tableName: string) => {
        setMappings(prev => {
            const next = { ...prev };
            delete next[tableName];
            return next;
        });
    };

    const handleSave = () => {
        latestOnSave.current(mappings);
        latestOnClose.current();
    };

    return (
        <McDialog
            open={open}
            closed={onClose}
            heading="Manage Data Mappings"
            width="800px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '600px', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <McInput
                        placeholder="Search tables..."
                        value={searchTerm}
                        input={(e: any) => setSearchTerm(e.target.value)}
                        style={{ flexGrow: 1 }}
                    >
                        <McIcon icon="magnifying-glass" slot="prefix" />
                    </McInput>
                </div>

                <div style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid var(--mds-color-neutral-20)', borderRadius: '4px' }}>
                    {filteredTables.map(tableName => {
                        const dictEntry = NAVAS_N4_DICTIONARY[tableName];
                        const userMap = mappings[tableName];
                        const currentBusinessName = userMap?.businessName || dictEntry?.title || '';

                        return (
                            <div key={tableName} style={{ borderBottom: '1px solid var(--mds-color-neutral-10)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', padding: '12px', gap: '12px', backgroundColor: 'var(--mds-color-neutral-05)' }}>
                                    <div style={{ cursor: 'pointer' }} onClick={() => handleExpandTable(tableName)}>
                                        <McIcon icon={expandedTable === tableName ? 'chevron-down' : 'chevron-right'} />
                                    </div>
                                    <div style={{ flexGrow: 1 }}>
                                        <div style={{ fontWeight: 'bold' }}>{tableName}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--mds-color-neutral-60)' }}>
                                            {dictEntry?.description || 'No description'}
                                        </div>
                                    </div>
                                    <div style={{ width: '300px' }}>
                                        <McInput
                                            title="Business Name"
                                            value={currentBusinessName}
                                            input={(e: any) => handleTableMappingChange(tableName, e.target.value)}
                                            placeholder={dictEntry?.title || tableName}
                                        />
                                    </div>
                                    <McTooltip>
                                        <McButton variant="plain" fit="small" onClick={() => handleResetTable(tableName)} disabled={!userMap}>
                                            <McIcon icon="arrow-anti-clockwise" />
                                        </McButton>
                                        <span>Reset to Default</span>
                                    </McTooltip>
                                </div>

                                {expandedTable === tableName && (
                                    <div style={{ padding: '16px', borderTop: '1px solid var(--mds-color-neutral-10)' }}>
                                        {loadingColumns === tableName ? (
                                            <McLoadingIndicator fit="small" />
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                {tableColumns[tableName]?.length > 0 ? (
                                                    tableColumns[tableName].map(colName => {
                                                        const colMap = userMap?.columns?.[colName];
                                                        return (
                                                            <div key={colName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <div style={{ width: '150px', fontSize: '0.9rem', wordBreak: 'break-all' }}>{colName}</div>
                                                                <McInput
                                                                    value={colMap || ''}
                                                                    placeholder={colName}
                                                                    input={(e: any) => handleColumnMappingChange(tableName, colName, e.target.value)}
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div style={{ gridColumn: '1 / -1', color: 'var(--mds-color-neutral-60)' }}>
                                                        No columns found. Query a terminal to load columns.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div slot="secondaryAction">
                    <McButton variant="outlined" onClick={() => latestOnClose.current()}>Cancel</McButton>
                </div>
                <div slot="primaryAction">
                    <McButton onClick={handleSave}>Save Changes</McButton>
                </div>
            </div>
        </McDialog>
    );
};
